import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  Drawer, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Divider,
  AppBar,
  Toolbar,
  IconButton,
  Container,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  Snackbar,
  Alert as MuiAlert,
  Switch,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Tabs,
  Tab,
  TablePagination,
  useTheme
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import PeopleIcon from '@mui/icons-material/People';
import BarChartIcon from '@mui/icons-material/BarChart';
import FlagIcon from '@mui/icons-material/Flag';
import SettingsIcon from '@mui/icons-material/Settings';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PostAddIcon from '@mui/icons-material/PostAdd';
import ReportIcon from '@mui/icons-material/Report';
import PersonIcon from '@mui/icons-material/Person';
import BackupIcon from '@mui/icons-material/Backup';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import ReplayIcon from '@mui/icons-material/Replay';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import InfoIcon from '@mui/icons-material/Info';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import BuildIcon from '@mui/icons-material/Build';
import SecurityIcon from '@mui/icons-material/Security';
import EventNoteIcon from '@mui/icons-material/EventNote';
import { useAuth } from '../contexts/AuthContext';
import UserManagement from '../components/Admin/UserManagement';
import ReportManagement from '../components/Admin/ReportManagement';
import CorporateManagement from '../components/Admin/CorporateManagement';
import ContentManagement from '../components/Admin/ContentManagement';
import UserDemographics from '../components/Admin/UserDemographics';
import ReviewAnalytics from '../components/Admin/ReviewAnalytics';
import ReservationAnalytics from '../components/Admin/ReservationAnalytics';
import Security from './admin/Security';
import './Dashboard.css';
import axios from 'axios';
import config from '../config';
import moment from 'moment-timezone';
import 'moment/locale/ja';

// moment の設定
moment.locale('ja');
moment.tz.setDefault('Asia/Tokyo');

// スナックバーのアラートコンポーネント
const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const Dashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const query = new URLSearchParams(location.search);
  const pageParam = query.get('page');
  
  const [activeMenuItem, setActiveMenuItem] = useState(pageParam || 'overview');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userStats, setUserStats] = useState({
    total: 0,
    active: 0,
    new: 0
  });
  const [contentStats, setContentStats] = useState({
    posts: 0,
    comments: 0,
    reports: 0,
    pendingReports: 0
  });
  const { currentUser, isLoggedIn, checkPermission, maintenanceMode, maintenanceEndTime, maintenanceContactUrl, toggleMaintenanceMode } = useAuth();
  
  // スナックバー関連の状態
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info' // 'error', 'warning', 'info', 'success'
  });
  
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };
  
  const showMessage = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };
  
  const drawerWidth = 240;

  // メニュー項目がクリックされたときの処理
  const handleMenuItemClick = (menuItem) => {
    setActiveMenuItem(menuItem);
    setMobileOpen(false);
    
    // URLのクエリパラメータを更新
    navigate(`/dashboard?page=${menuItem}`, { replace: true });
    
    // 各コンポーネントに移動したときもデータを更新
    fetchDashboardData();
  };
  
  // 権限チェック
  useEffect(() => {
    // ログインしていない場合はログインページにリダイレクト
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    
    // 管理者またはモデレーター権限がない場合は権限エラーページにリダイレクト
    if (!checkPermission('ADMIN_FUNCTIONS')) {
      
      navigate('/unauthorized');
      return;
    } else {
      
    }
    
    // URLからページパラメータを取得して状態を更新
    const pageParam = new URLSearchParams(location.search).get('page');
    if (pageParam && pageParam !== activeMenuItem) {
      setActiveMenuItem(pageParam);
    } else if (!pageParam && activeMenuItem !== 'overview') {
      // ページパラメータがない場合はoverviewに設定し、URLを更新
      navigate('/dashboard?page=overview', { replace: true });
    }
    
    // ダッシュボードデータの読み込み
    fetchDashboardData();
  }, [isLoggedIn, currentUser, navigate, checkPermission, location.search]);
  
  // アクティブなメニュー項目が変更されたときにデータを更新（概要画面のとき）
  useEffect(() => {
    if (activeMenuItem === 'overview') {
      fetchDashboardData();
    }
  }, [activeMenuItem, location.search]);
  
  // ダッシュボードの統計データを取得
  const fetchDashboardData = async () => {
    try {
      // データ読み込み中のフラグを設定
      setLoading(true);
      
      // APIからデータを取得
      const response = await axios.get(`${config.API_BASE_URL}/api/stats/overview`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.data.status === 'success') {
        const { data } = response.data;
        
        // ユーザー統計
        if (data.user_stats) {
          setUserStats({
            total: data.user_stats.total_users || 0,
            active: data.user_stats.active_users || 0,
            new: data.user_stats.new_users || 0
          });
        }
        
        // コンテンツ統計
        if (data.content_stats) {
          setContentStats({
            posts: data.content_stats.total_posts || 0,
            comments: data.content_stats.total_comments || 0,
            reports: data.content_stats.total_reports || 0,
            pendingReports: data.content_stats.pending_reports || 0
          });
        }
      } else {
        
        showMessage('データ取得に失敗しました', 'error');
      }
    } catch (error) {
      
      showMessage('サーバーとの通信に失敗しました', 'error');
    } finally {
      // データ読み込み完了
      setLoading(false);
    }
  };

  // モバイルでのドロワー開閉
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  
  // サイドメニューの内容
  const drawer = (
    <Box>
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" color="primary">
          管理ダッシュボード
        </Typography>
      </Box>
      <Divider />
      <List>
        <ListItem 
          button 
          selected={activeMenuItem === 'overview'} 
          onClick={() => handleMenuItemClick('overview')}
        >
          <ListItemIcon>
            <DashboardIcon />
          </ListItemIcon>
          <ListItemText primary="概要" />
        </ListItem>
        
        <ListItem 
          button 
          selected={activeMenuItem === 'users'} 
          onClick={() => handleMenuItemClick('users')}
        >
          <ListItemIcon>
            <PeopleIcon />
          </ListItemIcon>
          <ListItemText primary="ユーザー管理" />
        </ListItem>
        
        <ListItem 
          button 
          selected={activeMenuItem === 'content'} 
          onClick={() => handleMenuItemClick('content')}
        >
          <ListItemIcon>
            <PostAddIcon />
          </ListItemIcon>
          <ListItemText primary="企業管理" />
        </ListItem>
        
        <ListItem 
          button 
          selected={activeMenuItem === 'contentManagement'} 
          onClick={() => handleMenuItemClick('contentManagement')}
        >
          <ListItemIcon>
            <PostAddIcon />
          </ListItemIcon>
          <ListItemText primary="コンテンツ管理" />
        </ListItem>
        
        <ListItem 
          button 
          selected={activeMenuItem === 'reports'} 
          onClick={() => handleMenuItemClick('reports')}
        >
          <ListItemIcon>
            <ReportIcon />
          </ListItemIcon>
          <ListItemText primary="報告管理" />
        </ListItem>
        
        <ListItem 
          button 
          selected={activeMenuItem === 'security'} 
          onClick={() => handleMenuItemClick('security')}
        >
          <ListItemIcon>
            <SecurityIcon />
          </ListItemIcon>
          <ListItemText primary="セキュリティ" />
        </ListItem>
        
        <ListItem 
          button 
          selected={activeMenuItem === 'reviews'} 
          onClick={() => handleMenuItemClick('reviews')}
        >
          <ListItemIcon>
            <BarChartIcon />
          </ListItemIcon>
          <ListItemText primary="クチコミ管理" />
        </ListItem>

        <ListItem 
          button 
          selected={activeMenuItem === 'reservations'} 
          onClick={() => handleMenuItemClick('reservations')}
        >
          <ListItemIcon>
            <EventNoteIcon />
          </ListItemIcon>
          <ListItemText primary="予約統計" />
        </ListItem>
      </List>
      <Divider />
      <List>
        <ListItem 
          button 
          selected={activeMenuItem === 'statistics'} 
          onClick={() => handleMenuItemClick('statistics')}
        >
          <ListItemIcon>
            <BarChartIcon />
          </ListItemIcon>
          <ListItemText primary="統計" />
        </ListItem>
        
        <ListItem 
          button 
          selected={activeMenuItem === 'settings'} 
          onClick={() => handleMenuItemClick('settings')}
        >
          <ListItemIcon>
            <SettingsIcon />
          </ListItemIcon>
          <ListItemText primary="設定" />
        </ListItem>
      </List>
    </Box>
  );
  
  // アクティブなコンテンツを表示
  const renderContent = () => {
    switch (activeMenuItem) {
      case 'users':
        return (
          <UserManagement />
        );
      case 'overview':
        return (
          <Box>
            <Typography variant="h4" sx={{ mb: 3 }}>
              ダッシュボード概要
            </Typography>
            
            <Grid container spacing={3}>
              {/* ユーザー統計 */}
              <Grid item xs={12} md={4}>
                <Card>
                  <CardHeader title="ユーザー" />
                  <CardContent>
                    {loading ? (
                      <CircularProgress size={40} />
                    ) : (
                      <>
                        <Typography variant="h3" color="primary">
                          {userStats.total}
                        </Typography>
                        <Grid container sx={{ mt: 2 }}>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="textSecondary">
                              アクティブ
                            </Typography>
                            <Typography variant="h6">
                              {userStats.active}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="textSecondary">
                              新規（今月）
                            </Typography>
                            <Typography variant="h6">
                              {userStats.new}
                            </Typography>
                          </Grid>
                        </Grid>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              {/* 投稿統計 */}
              <Grid item xs={12} md={4}>
                <Card>
                  <CardHeader title="投稿" />
                  <CardContent>
                    {loading ? (
                      <CircularProgress size={40} />
                    ) : (
                      <>
                        <Typography variant="h3" color="primary">
                          {contentStats.posts}
                        </Typography>
                        <Grid container sx={{ mt: 2 }}>
                          <Grid item xs={12}>
                            <Typography variant="body2" color="textSecondary">
                              コメント数
                            </Typography>
                            <Typography variant="h6">
                              {contentStats.comments}
                            </Typography>
                          </Grid>
                        </Grid>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              {/* 報告統計 */}
              <Grid item xs={12} md={4}>
                <Card>
                  <CardHeader title="報告" />
                  <CardContent>
                    {loading ? (
                      <CircularProgress size={40} />
                    ) : (
                      <>
                        <Typography variant="h3" color="error">
                          {contentStats.reports}
                        </Typography>
                        <Grid container sx={{ mt: 2 }}>
                          <Grid item xs={12}>
                            <Typography variant="body2" color="textSecondary">
                              対応が必要な報告
                            </Typography>
                            <Typography variant="h6" color="error">
                              {contentStats.pendingReports}
                            </Typography>
                          </Grid>
                        </Grid>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              {/* クイックリンク */}
              <Grid item xs={12}>
                <Card>
                  <CardHeader 
                    title="クイックアクション" 
                    action={
                      <IconButton onClick={fetchDashboardData} title="データを更新">
                        <ReplayIcon />
                      </IconButton>
                    }
                  />
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={4} lg={2}>
                        <Paper 
                          elevation={2} 
                          sx={{ p: 2, textAlign: 'center', cursor: 'pointer' }}
                          onClick={() => handleMenuItemClick('users')}
                        >
                          <PeopleIcon color="primary" sx={{ fontSize: 40 }} />
                          <Typography>ユーザー管理</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4} lg={2}>
                        <Paper 
                          elevation={2} 
                          sx={{ p: 2, textAlign: 'center', cursor: 'pointer' }}
                          onClick={() => handleMenuItemClick('content')}
                        >
                          <PostAddIcon color="primary" sx={{ fontSize: 40 }} />
                          <Typography>企業管理</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4} lg={2}>
                        <Paper 
                          elevation={2} 
                          sx={{ p: 2, textAlign: 'center', cursor: 'pointer' }}
                          onClick={() => handleMenuItemClick('contentManagement')}
                        >
                          <PostAddIcon color="primary" sx={{ fontSize: 40 }} />
                          <Typography>コンテンツ管理</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4} lg={2}>
                        <Paper 
                          elevation={2} 
                          sx={{ p: 2, textAlign: 'center', cursor: 'pointer' }}
                          onClick={() => handleMenuItemClick('reports')}
                        >
                          <FlagIcon color="primary" sx={{ fontSize: 40 }} />
                          <Typography>報告確認</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4} lg={2}>
                        <Paper 
                          elevation={2} 
                          sx={{ p: 2, textAlign: 'center', cursor: 'pointer' }}
                          onClick={() => handleMenuItemClick('statistics')}
                        >
                          <BarChartIcon color="primary" sx={{ fontSize: 40 }} />
                          <Typography>統計分析</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4} lg={2}>
                        <Paper 
                          elevation={2} 
                          sx={{ p: 2, textAlign: 'center', cursor: 'pointer' }}
                          onClick={() => handleMenuItemClick('security')}
                        >
                          <SecurityIcon color="primary" sx={{ fontSize: 40 }} />
                          <Typography>セキュリティ</Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        );
      case 'content':
        return (
          <Box>
            <Typography variant="h4" sx={{ mb: 3 }}>
              企業管理
            </Typography>
            <CorporateManagement showMessage={showMessage} />
          </Box>
        );
      case 'contentManagement':
        return (
          <Box>
            <Typography variant="h4" sx={{ mb: 3 }}>
              コンテンツ管理
            </Typography>
            <ContentManagement showMessage={showMessage} />
          </Box>
        );
      case 'reports':
        return (
          <Box>
            <Typography variant="h4" sx={{ mb: 3 }}>
              報告管理
            </Typography>
            <ReportManagement showMessage={showMessage} />
          </Box>
        );
      case 'statistics':
        return (
          <Box>
            <Typography variant="h4" sx={{ mb: 3 }}>
              統計
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                {/* ユーザー属性統計 */}
                <UserDemographics showMessage={showMessage} />
              </Grid>
              <Grid item xs={12}>
                {/* 詳細統計情報 */}
                  <StatisticsOverview />
              </Grid>
            </Grid>
          </Box>
        );
      case 'settings':
        return (
          <Box>
            <Typography variant="h4" sx={{ mb: 3 }}>
              設定
            </Typography>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                メンテナンスモード
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="body1" sx={{ flexGrow: 1 }}>
                  メンテナンスモードを有効にすると、管理者以外のユーザーはサイトにアクセスできなくなります。
                  メンテナンス作業中や緊急時に使用してください。
                </Typography>
                <Button 
                  variant={maintenanceMode ? "contained" : "outlined"}
                  color={maintenanceMode ? "error" : "primary"}
                  onClick={() => {
                    
                    const newState = toggleMaintenanceMode();
                    
                    // スナックバーでユーザーに通知
                    const message = !maintenanceMode ? 
                      'メンテナンスモードを有効にしました。管理者以外のユーザーはアクセスできません。' : 
                      'メンテナンスモードを無効にしました。通常運用に戻ります。';
                    showMessage(message, !maintenanceMode ? 'warning' : 'success');
                  }}
                  sx={{ ml: 2 }}
                >
                  {maintenanceMode ? "無効にする" : "有効にする"}
                </Button>
              </Box>
              {maintenanceMode && (
                <Box sx={{ backgroundColor: 'error.light', color: 'error.contrastText', p: 2, borderRadius: 1, mb: 2 }}>
                  <Typography variant="body2" fontWeight="bold">
                    注意: 現在メンテナンスモードが有効です。管理者以外のユーザーはサイトにアクセスできません。
                  </Typography>
                </Box>
              )}
              
              {/* メンテナンスモード追加設定 */}
              <TextField
                label="メンテナンス終了予定"
                variant="outlined"
                fullWidth
                margin="normal"
                value={maintenanceEndTime}
                onChange={(e) => {
                  const updatedSettings = toggleMaintenanceMode(maintenanceMode, e.target.value, maintenanceContactUrl);
                  
                  showMessage('メンテナンス終了予定時間を更新しました', 'info');
                }}
                helperText="例：2023年10月1日 15:00頃、または「調整中」など"
              />
              
              <TextField
                label="お問い合わせ先URL"
                variant="outlined"
                fullWidth
                margin="normal"
                value={maintenanceContactUrl}
                onChange={(e) => {
                  const updatedSettings = toggleMaintenanceMode(maintenanceMode, maintenanceEndTime, e.target.value);
                  
                  showMessage('お問い合わせ先URLを更新しました', 'info');
                }}
                helperText="例：mailto:support@example.com、https://support.example.com など"
              />
            </Paper>

            {/* バックアップ設定 */}
            <BackupSettings showMessage={showMessage} />
          </Box>
        );
      case 'security':
        return (
          <Box>
            <Typography variant="h4" sx={{ mb: 3 }}>
              セキュリティ
            </Typography>
            <Security showMessage={showMessage} />
          </Box>
        );
      case 'reviews':
        return (
          <Box>
            <Typography variant="h4" sx={{ mb: 3 }}>
              クチコミ管理
            </Typography>
            <ReviewAnalytics />
          </Box>
        );
      case 'reservations':
        return (
          <Box>
            <Typography variant="h4" sx={{ mb: 3 }}>
              予約統計
            </Typography>
            <ReservationAnalytics />
          </Box>
        );
      default:
        return <div>ページが見つかりません</div>;
    }
  };
  
  return (
    <Box sx={{ display: 'flex' }}>
      {/* アプリバー */}
      <AppBar
        position="fixed"
        sx={{
          width: '100%', // 常に画面幅いっぱいに
          ml: { xs: 0, sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div">
              {(() => {
                switch (activeMenuItem) {
                  case 'overview': return 'ダッシュボード概要';
                  case 'users': return 'ユーザー管理';
                  case 'content': return '企業管理';
                  case 'reports': return '報告管理';
                  case 'security': return 'セキュリティ';
                  case 'reviews': return 'クチコミ管理';
                  case 'reservations': return '予約統計';
                  case 'statistics': return '統計';
                  case 'settings': return '設定';
                  default: return 'ダッシュボード';
                }
              })()}
            </Typography>
          </Box>
          
          {/* プロフィールリンクボタン */}
          <Button
            color="inherit"
            component={Link}
            to={`/profile/${currentUser.username}`}
            startIcon={<PersonIcon />}
            sx={{ 
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            プロフィールを表示
          </Button>
        </Toolbar>
      </AppBar>
      
      {/* サイドメニュー */}
      <Box
        component="nav"
        sx={{ 
          width: { xs: 0, sm: drawerWidth }, 
          flexShrink: { sm: 0 },
          display: { xs: 'none', sm: 'block' } // モバイル表示では非表示、デスクトップ表示では表示
        }}
        aria-label="dashboard menu"
      >
        {/* モバイル用ドロワー */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // モバイルでのパフォーマンス向上
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        
        {/* デスクトップ用ドロワー */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              border: '1px solid rgba(0, 0, 0, 0.12)'
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      
      {/* メインコンテンツ */}
      <Box
        component="main"
        sx={{ 
          flexGrow: 1, 
          p: { xs: 2, sm: 3 }, // モバイルではパディングを小さく
          width: { xs: '100%', sm: `calc(100% - ${drawerWidth}px)` }, // サイドバーの幅を考慮
          marginTop: '64px', // ツールバーの高さ
          marginLeft: { xs: 0, sm: `${drawerWidth}px` }, // モバイルでは左余白なし
          boxSizing: 'border-box'
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 } }}> {/* コンテナのパディングも調整 */}
          {renderContent()}
        </Container>
      </Box>
      
      {/* スナックバーメッセージ */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// BackupSettingsコンポーネントを追加
const BackupSettings = ({ showMessage }) => {
  const [loading, setLoading] = useState(false);
  const [backupSettings, setBackupSettings] = useState({
    enabled: true,
    interval_days: 2,
    retention_days: 7,
    backup_dir: '',
    db_path: ''
  });
  const [history, setHistory] = useState([]);
  const [diskUsage, setDiskUsage] = useState('0 MB');
  const [historyCount, setHistoryCount] = useState(0);
  const [runningBackup, setRunningBackup] = useState(false);
  const [deletingBackup, setDeletingBackup] = useState(false);
  const [confirmDeleteBackup, setConfirmDeleteBackup] = useState(null);

  // バックアップ設定を取得
  const fetchBackupSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await fetch(`${config.API_BASE_URL}/api/settings/backup`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('バックアップ設定の取得に失敗しました');
      }

      const data = await response.json();
      setBackupSettings(data.settings || {});
      setHistory(data.history || []);
      setDiskUsage(data.disk_usage_formatted || '0 MB');
      setHistoryCount(data.history_count || 0);
    } catch (error) {
      
      showMessage(`エラー: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // コンポーネントマウント時に設定を取得
  useEffect(() => {
    fetchBackupSettings();
  }, []);

  // バックアップ設定を更新
  const updateBackupSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await fetch(`${config.API_BASE_URL}/api/settings/backup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(backupSettings)
      });

      if (!response.ok) {
        throw new Error('バックアップ設定の更新に失敗しました');
      }

      const data = await response.json();
      showMessage('バックアップ設定を更新しました', 'success');
      // 更新された設定を反映
      setBackupSettings(data.settings || backupSettings);
    } catch (error) {
      
      showMessage(`エラー: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // 手動バックアップを実行
  const runBackup = async () => {
    try {
      setRunningBackup(true);
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await fetch(`${config.API_BASE_URL}/api/settings/backup/run`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('バックアップの実行に失敗しました');
      }

      const data = await response.json();
      showMessage(data.message || 'バックアップを実行しました', 'success');
      
      // バックアップが終わったら最新の情報を取得
      await fetchBackupSettings();
    } catch (error) {
      
      showMessage(`エラー: ${error.message}`, 'error');
    } finally {
      setRunningBackup(false);
    }
  };

  // バックアップをダウンロード
  const downloadBackup = (filename) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      showMessage('ログインが必要です', 'error');
      return;
    }
    
    // ダウンロードURLを生成
    const downloadUrl = `${config.API_BASE_URL}/api/settings/backup/download/${filename}`;
    
    // 新しいタブでダウンロードを開始
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.target = '_blank';
    link.setAttribute('download', filename);
    
    // 認証トークンをヘッダーに追加できないため、別の方法でダウンロード
    fetch(downloadUrl, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('ダウンロードに失敗しました');
      }
      return response.blob();
    })
    .then(blob => {
      const url = window.URL.createObjectURL(blob);
      link.href = url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showMessage('ダウンロードを開始しました', 'success');
    })
    .catch(error => {
      
      showMessage(`エラー: ${error.message}`, 'error');
    });
  };

  // バックアップを削除
  const deleteBackup = async (filename) => {
    try {
      setDeletingBackup(true);
      const token = localStorage.getItem('authToken');
      if (!token) {
        showMessage('ログインが必要です', 'error');
        return;
      }
      
      const response = await fetch(`${config.API_BASE_URL}/api/settings/backup/delete/${filename}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('バックアップの削除に失敗しました');
      }
      
      const data = await response.json();
      showMessage(data.message || 'バックアップを削除しました', 'success');
      
      // 削除後に履歴を更新
      await fetchBackupSettings();
    } catch (error) {
      
      showMessage(`エラー: ${error.message}`, 'error');
    } finally {
      setDeletingBackup(false);
      setConfirmDeleteBackup(null);
    }
  };

  // 設定変更ハンドラー
  const handleSettingChange = (setting, value) => {
    setBackupSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        データベースバックアップ
      </Typography>
      
      {/* バックアップ状態の概要 */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.light', color: 'primary.contrastText' }}>
              <Typography variant="h6">バックアップ間隔</Typography>
              <Typography variant="h4">{backupSettings.interval_days}日</Typography>
              <Typography variant="body2">ごとに自動実行</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'secondary.light', color: 'secondary.contrastText' }}>
              <Typography variant="h6">保存期間</Typography>
              <Typography variant="h4">{backupSettings.retention_days}日間</Typography>
              <Typography variant="body2">古いバックアップは自動削除</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light', color: 'info.contrastText' }}>
              <Typography variant="h6">ディスク使用量</Typography>
              <Typography variant="h4">{diskUsage}</Typography>
              <Typography variant="body2">バックアップ数: {historyCount}件</Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>
      
      {/* バックアップ設定フォーム */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                自動バックアップ
              </Typography>
              <Switch
                checked={backupSettings.enabled}
                onChange={(e) => handleSettingChange('enabled', e.target.checked)}
                color="primary"
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              有効にすると、設定した間隔でデータベースが自動的にバックアップされます。
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              label="バックアップ間隔（日数）"
              type="number"
              fullWidth
              value={backupSettings.interval_days}
              onChange={(e) => handleSettingChange('interval_days', Number(e.target.value))}
              InputProps={{ inputProps: { min: 1, max: 30 } }}
              disabled={!backupSettings.enabled || loading}
              helperText="1～30日の範囲で指定してください"
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              label="バックアップ保持期間（日数）"
              type="number"
              fullWidth
              value={backupSettings.retention_days}
              onChange={(e) => handleSettingChange('retention_days', Number(e.target.value))}
              InputProps={{ inputProps: { min: 1, max: 365 } }}
              disabled={loading}
              helperText="古いバックアップを保持する期間（日数）"
            />
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={updateBackupSettings}
                disabled={loading}
                startIcon={<SettingsIcon />}
              >
                設定を保存
              </Button>
              
              <Button
                variant="contained"
                color="secondary"
                onClick={runBackup}
                disabled={loading || runningBackup}
                startIcon={runningBackup ? <CircularProgress size={20} color="inherit" /> : <BackupIcon />}
              >
                {runningBackup ? "バックアップ実行中..." : "今すぐバックアップ"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
      
      {/* バックアップ履歴 */}
      {history.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
            最近のバックアップ
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>日時</TableCell>
                  <TableCell>ファイル名</TableCell>
                  <TableCell align="right">サイズ</TableCell>
                  <TableCell align="center">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((backup) => (
                  <TableRow key={backup.timestamp}>
                    <TableCell>{moment.utc(backup.date).tz('Asia/Tokyo').format('YYYY年M月D日 HH:mm')}</TableCell>
                    <TableCell>{backup.filename}</TableCell>
                    <TableCell align="right">{(backup.size / (1024 * 1024)).toFixed(2)} MB</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        {/* ダウンロードボタン */}
                        <IconButton 
                          color="primary" 
                          onClick={() => downloadBackup(backup.filename)}
                          title="ダウンロード"
                          size="small"
                          sx={{ mx: 0.5 }}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                        
                        {/* 削除ボタン */}
                        {confirmDeleteBackup === backup.filename ? (
                          <>
                            <Button 
                              color="error" 
                              variant="contained"
                              size="small"
                              sx={{ mx: 0.5, fontSize: '0.7rem' }}
                              onClick={() => deleteBackup(backup.filename)}
                              disabled={deletingBackup}
                            >
                              確認
                            </Button>
                            <Button 
                              color="inherit" 
                              variant="outlined"
                              size="small"
                              sx={{ mx: 0.5, fontSize: '0.7rem' }}
                              onClick={() => setConfirmDeleteBackup(null)}
                              disabled={deletingBackup}
                            >
                              キャンセル
                            </Button>
                          </>
                        ) : (
                          <IconButton 
                            color="error" 
                            onClick={() => setConfirmDeleteBackup(backup.filename)}
                            title="削除"
                            size="small"
                            sx={{ mx: 0.5 }}
                            disabled={deletingBackup}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      
      {/* ローディング中 */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <CircularProgress />
        </Box>
      )}
    </Paper>
  );
};

// 統計情報概要コンポーネント
const StatisticsOverview = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    // 実データを取得する
    fetchDetailedStats();
  }, []);

  // ダミーデータ生成
  const generateDummyStats = () => {
    return {
      profile_views_by_user: [
        { username: 'user1', display_name: '太郎', profile_views: 120, user_id: 1 },
        { username: 'user2', display_name: '花子', profile_views: 85, user_id: 2 },
        { username: 'user3', display_name: '次郎', profile_views: 64, user_id: 3 },
      ],
      post_count_by_user: [
        { username: 'user1', display_name: '太郎', post_count: 45, user_id: 1 },
        { username: 'user3', display_name: '次郎', post_count: 32, user_id: 3 },
        { username: 'user2', display_name: '花子', post_count: 28, user_id: 2 },
      ],
      likes_by_post: [
        { post_id: 101, post_text: '今日は良い天気ですね', username: 'user1', like_count: 24 },
        { post_id: 102, post_text: '新商品が入荷しました', username: 'user2', like_count: 18 },
        { post_id: 103, post_text: '明日の予定について', username: 'user3', like_count: 15 },
      ],
      likes_by_user: [
        { username: 'user2', display_name: '花子', like_count: 78, user_id: 2 },
        { username: 'user1', display_name: '太郎', like_count: 65, user_id: 1 },
        { username: 'user3', display_name: '次郎', like_count: 42, user_id: 3 },
      ]
    };
  };

  const fetchDetailedStats = async () => {
    setLoading(true);
    try {
      // 詳細統計データを取得
      const response = await axios.get(`${config.API_BASE_URL}/api/stats/detailed`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`
        },
        params: {
          offset: page * rowsPerPage,
          limit: rowsPerPage
        }
      });

      if (response.data.status === 'success') {
        setStats(response.data.data);
      } else {
        setError('統計データの取得に失敗しました');
      }
    } catch (error) {
      console.error('統計データエラー:', error.response?.data || error.message);
      setError(`サーバーとの通信に失敗しました: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    setPage(0); // タブ切り替え時はページをリセット
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!stats) {
    return (
      <Alert severity="info">
        データがありません
      </Alert>
    );
  }

  // タブのラベルとそれに対応するデータのマッピング
  const tabData = [
    { label: "プロフィール閲覧数（ユーザー別）", data: stats.profile_views_by_user || [], empty: !stats.profile_views_by_user || stats.profile_views_by_user.length === 0 },
    { label: "投稿数（ユーザー別）", data: stats.post_count_by_user || [], empty: !stats.post_count_by_user || stats.post_count_by_user.length === 0 },
    { label: "いいね（投稿別）", data: stats.likes_by_post || [], empty: !stats.likes_by_post || stats.likes_by_post.length === 0 },
    { label: "いいね（ユーザー別）", data: stats.likes_by_user || [], empty: !stats.likes_by_user || stats.likes_by_user.length === 0 },
    { label: "コメント数（投稿別）", data: stats.comments_by_post || [], empty: !stats.comments_by_post || stats.comments_by_post.length === 0 },
    { label: "コメント数（ユーザー別）", data: stats.comments_by_user || [], empty: !stats.comments_by_user || stats.comments_by_user.length === 0 },
    { label: "ブックマーク（投稿別）", data: stats.bookmarks_by_post || [], empty: !stats.bookmarks_by_post || stats.bookmarks_by_post.length === 0 },
    { label: "ブックマーク（ユーザー別）", data: stats.bookmarks_by_user || [], empty: !stats.bookmarks_by_user || stats.bookmarks_by_user.length === 0 },
    { label: "フォロワー数（ユーザー別）", data: stats.followers_by_user || [], empty: !stats.followers_by_user || stats.followers_by_user.length === 0 },
    { label: "ビュー数（投稿別）", data: stats.views_by_post || [], empty: !stats.views_by_post || stats.views_by_post.length === 0 },
    { label: "ビュー数（ユーザー別）", data: stats.views_by_user || [], empty: !stats.views_by_user || stats.views_by_user.length === 0 }
  ];

  // 選択されたタブのデータを表示
  const renderTable = () => {
    const currentTabData = tabData[selectedTab];
    
    if (currentTabData.empty) {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          この統計情報のデータはありません
        </Alert>
      );
    }
    
    if (!currentTabData.data || currentTabData.data.length === 0) {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          データがありません
        </Alert>
      );
    }

    // タブによってテーブルの列を決定
    let columns = [];
    switch (selectedTab) {
      case 0: // プロフィール閲覧数（ユーザー別）
        columns = [
          { id: 'username', label: 'ユーザー名' },
          { id: 'display_name', label: '表示名' },
          { id: 'profile_views', label: '閲覧数', align: 'right' }
        ];
        break;
      case 1: // 投稿数（ユーザー別）
        columns = [
          { id: 'username', label: 'ユーザー名' },
          { id: 'display_name', label: '表示名' },
          { id: 'post_count', label: '投稿数', align: 'right' }
        ];
        break;
      case 2: // いいね（投稿別）
        columns = [
          { id: 'post_id', label: '投稿ID' },
          { id: 'post_text', label: '投稿内容' },
          { id: 'username', label: '投稿者' },
          { id: 'like_count', label: 'いいね数', align: 'right' }
        ];
        break;
      case 3: // いいね（ユーザー別）
        columns = [
          { id: 'username', label: 'ユーザー名' },
          { id: 'display_name', label: '表示名' },
          { id: 'like_count', label: 'いいね数', align: 'right' }
        ];
        break;
      case 4: // コメント数（投稿別）
        columns = [
          { id: 'post_id', label: '投稿ID' },
          { id: 'post_text', label: '投稿内容' },
          { id: 'username', label: '投稿者' },
          { id: 'comment_count', label: 'コメント数', align: 'right' }
        ];
        break;
      case 5: // コメント数（ユーザー別）
        columns = [
          { id: 'username', label: 'ユーザー名' },
          { id: 'display_name', label: '表示名' },
          { id: 'comment_count', label: 'コメント数', align: 'right' }
        ];
        break;
      case 6: // ブックマーク（投稿別）
        columns = [
          { id: 'post_id', label: '投稿ID' },
          { id: 'post_text', label: '投稿内容' },
          { id: 'username', label: '投稿者' },
          { id: 'bookmark_count', label: 'ブックマーク数', align: 'right' }
        ];
        break;
      case 7: // ブックマーク（ユーザー別）
        columns = [
          { id: 'username', label: 'ユーザー名' },
          { id: 'display_name', label: '表示名' },
          { id: 'bookmark_count', label: 'ブックマーク数', align: 'right' }
        ];
        break;
      case 8: // フォロワー数（ユーザー別）
        columns = [
          { id: 'username', label: 'ユーザー名' },
          { id: 'display_name', label: '表示名' },
          { id: 'follower_count', label: 'フォロワー数', align: 'right' }
        ];
        break;
      case 9: // ビュー数（投稿別）
        columns = [
          { id: 'post_id', label: '投稿ID' },
          { id: 'post_text', label: '投稿内容' },
          { id: 'username', label: '投稿者' },
          { id: 'view_count', label: 'ビュー数', align: 'right' }
        ];
        break;
      case 10: // ビュー数（ユーザー別）
        columns = [
          { id: 'username', label: 'ユーザー名' },
          { id: 'display_name', label: '表示名' },
          { id: 'total_views', label: '合計ビュー数', align: 'right' }
        ];
        break;
      default:
        columns = [];
    }

    return (
      <Box sx={{ mt: 2 }}>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align || 'left'}
                    style={{ fontWeight: 'bold' }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {currentTabData.data.map((row, index) => (
                <TableRow key={index} hover>
                  {columns.map((column) => (
                    <TableCell key={column.id} align={column.align || 'left'}>
                      {column.id === 'username' && row.user_id ? (
                        <Link to={`/profile/${row.user_id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                          @{row[column.id]}
                        </Link>
                      ) : column.id === 'post_id' ? (
                        <Link to={`/post/${row[column.id]}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                          #{row[column.id]}
                        </Link>
                      ) : (
                        row[column.id]
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <TablePagination
            component="div"
            count={-1} // 合計件数は不明のため-1
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="表示件数:"
            labelDisplayedRows={({ from, to }) => `${from}-${to}`}
            nextIconButtonProps={{ 
              disabled: !stats.pagination?.more && page > 0
            }}
          />
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={selectedTab} 
          onChange={handleTabChange} 
          variant="scrollable"
          scrollButtons="auto"
        >
          {tabData.map((tab, index) => (
            <Tab label={tab.label} key={index} />
          ))}
        </Tabs>
      </Box>
      {renderTable()}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
        <Button 
          variant="outlined" 
          startIcon={<ReplayIcon />} 
          onClick={fetchDetailedStats}
        >
          データを更新
        </Button>
      </Box>
    </Box>
  );
};

export default Dashboard; 